import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import {
    FaMapMarkerAlt,
    FaArrowRight,
    FaHotel,
    FaStar,
    FaCheck,
    FaChevronRight,
    FaCompass,
    FaShieldAlt,
} from "react-icons/fa";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";

export default function DestinationDetails() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [destination, setDestination] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDestination = async () => {
            try {
                const res = await axios.get(
                    `https://dubaihotelrates.com/api/destinations/${slug}`
                );

                setDestination(res.data);
            } catch (err) {
                console.error("Failed to fetch destination:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDestination();
    }, [slug]);

    /* ---------------------------------------
       LOADING
    --------------------------------------- */

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F4F8FC]">
                <Header />

                <div className="flex min-h-[70vh] items-center justify-center">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#DCE9F8] border-t-[#174EA6]" />

                        <p className="mt-4 text-sm font-medium text-slate-500">
                            Loading destination...
                        </p>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    /* ---------------------------------------
       NOT FOUND
    --------------------------------------- */

    if (!destination) {
        return (
            <div className="min-h-screen bg-[#F4F8FC]">
                <Header />

                <div className="flex min-h-[70vh] items-center justify-center px-4">
                    <div className="max-w-md text-center">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#EAF3FF]">
                            <FaCompass className="text-3xl text-[#174EA6]" />
                        </div>

                        <h1 className="mt-6 text-3xl font-extrabold text-[#0F172A]">
                            Destination Not Found
                        </h1>

                        <p className="mt-3 text-sm leading-6 text-slate-500">
                            We couldn't find the destination you're looking for.
                            Please explore our other Dubai destinations.
                        </p>

                        <button
                            onClick={() => navigate("/destinations")}
                            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#174EA6] to-[#FFC928] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
                        >
                            Explore Destinations
                            <FaArrowRight />
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    const hotels = destination.hotels || [];

    return (
        <div className="min-h-screen bg-[#F4F8FC] text-[#0F172A]">

            {/* =====================================================
                HERO
            ===================================================== */}

            <section className="relative overflow-hidden">

                <Hero
                    heightClass="h-[300px] sm:h-[330px] md:h-[370px] lg:h-[400px]"
                    title={destination.name}
                    backgroundImage={heroImg}
                    contentClass=""
                />

                {/* Breadcrumb - Bottom of Hero */}
                <div className="absolute bottom-5 left-0 right-0 z-20 sm:bottom-6">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                        <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-[#002B45]/70 px-4 py-2.5 text-xs font-medium text-white shadow-lg backdrop-blur-md">

                            <Link
                                to="/"
                                className="transition hover:text-[#FFC928]"
                            >
                                Home
                            </Link>

                            <FaChevronRight className="text-[8px] text-white/50" />

                            <Link
                                to="/destinations"
                                className="transition hover:text-[#FFC928]"
                            >
                                Destinations
                            </Link>

                            <FaChevronRight className="text-[8px] text-white/50" />

                            <span className="font-semibold text-[#FFC928]">
                                {destination.name}
                            </span>

                        </div>

                    </div>
                </div>

            </section>


            {/* =====================================================
                DESTINATION INTRO
            ===================================================== */}

            <section className="relative z-10 -mt-10 px-4 sm:px-6 lg:px-8">

                <div className="mx-auto max-w-7xl">

                    <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]">

                        <div className="grid lg:grid-cols-[1fr_auto]">

                            {/* Content */}
                            <div className="p-6 sm:p-8 lg:p-10">

                                <div className="flex flex-wrap items-center gap-3">

                                    <span className="inline-flex items-center gap-2 rounded-full bg-[#EAF3FF] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#174EA6]">
                                        <FaMapMarkerAlt />
                                        Dubai Destination
                                    </span>

                                    <span className="inline-flex items-center gap-2 rounded-full bg-[#FFF8D8] px-3 py-1.5 text-[11px] font-bold text-[#9A7600]">
                                        <FaHotel />
                                        {hotels.length} Hotels
                                    </span>

                                </div>

                                <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-4xl">
                                    Hotels in{" "}
                                    <span className="text-[#174EA6]">
                                        {destination.name}
                                    </span>
                                </h1>

                                <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-500 sm:text-base">
                                    Discover comfortable and carefully selected
                                    hotels in {destination.name}. Compare
                                    accommodation options, explore hotel
                                    facilities and find the perfect stay for
                                    your Dubai trip.
                                </p>

                                <div className="mt-6 flex flex-wrap gap-5">

                                    <div className="flex items-center gap-2">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF3FF]">
                                            <FaCheck className="text-sm text-[#174EA6]" />
                                        </div>

                                        <div>
                                            <p className="text-xs font-bold text-[#0F172A]">
                                                Best Hotel Deals
                                            </p>
                                            <p className="text-[10px] text-slate-400">
                                                Competitive rates
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF8D8]">
                                            <FaShieldAlt className="text-sm text-[#B68A00]" />
                                        </div>

                                        <div>
                                            <p className="text-xs font-bold text-[#0F172A]">
                                                Trusted Booking
                                            </p>
                                            <p className="text-[10px] text-slate-400">
                                                Secure reservation
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF3FF]">
                                            <FaHotel className="text-sm text-[#174EA6]" />
                                        </div>

                                        <div>
                                            <p className="text-xs font-bold text-[#0F172A]">
                                                Quality Stays
                                            </p>
                                            <p className="text-[10px] text-slate-400">
                                                Selected hotels
                                            </p>
                                        </div>
                                    </div>

                                </div>

                            </div>


                            {/* Hotel count panel */}
                            <div className="flex min-w-[220px] items-center justify-center border-t border-slate-100 bg-gradient-to-br from-[#F4F9FF] to-white p-8 lg:border-l lg:border-t-0">

                                <div className="text-center">

                                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#174EA6] to-[#002B45] shadow-lg">
                                        <FaHotel className="text-3xl text-white" />
                                    </div>

                                    <p className="mt-4 text-3xl font-extrabold text-[#0F172A]">
                                        {hotels.length}
                                    </p>

                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                                        Available Hotels
                                    </p>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </section>


            {/* =====================================================
                HOTELS SECTION
            ===================================================== */}

            <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">

                <div className="mx-auto max-w-7xl">

                    {/* Section heading */}
                    <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">

                        <div>

                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#174EA6]">
                                Explore Your Options
                            </p>

                            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-4xl">
                                Find Your Perfect Stay
                            </h2>

                            <div className="mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-[#174EA6] to-[#FFC928]" />

                        </div>

                        <p className="max-w-md text-sm leading-6 text-slate-500 md:text-right">
                            Browse our selection of hotels in{" "}
                            <span className="font-semibold text-[#174EA6]">
                                {destination.name}
                            </span>{" "}
                            and choose the stay that suits your trip.
                        </p>

                    </div>


                    {/* =================================================
                        HOTEL GRID
                    ================================================= */}

                    {hotels.length > 0 ? (

                        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">

                            {hotels.map((hotel, index) => {

                                const cleanDescription =
                                    hotel.short_description
                                        ?.replace(/<[^>]*>/g, "")
                                        ?.trim() ||
                                    "Enjoy a comfortable stay with convenient access to Dubai's attractions.";

                                const price = Math.round(
                                    Number(hotel.price_per_night || 0)
                                );

                                return (

                                    <article
                                        key={hotel.id}
                                        onClick={() =>
                                            navigate(`/hotels/${hotel.slug}`)
                                        }
                                        className="group cursor-pointer overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(15,23,42,0.12)]"
                                    >

                                        {/* ==========================
                                            IMAGE
                                        ========================== */}

                                        <div className="relative h-[245px] overflow-hidden">

                                            <img
                                                src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                                alt={hotel.name}
                                                className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                                            />

                                            {/* Image overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/80 via-transparent to-black/10" />


                                            {/* Number */}
                                            <div className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl bg-[#002B45]/85 text-xs font-extrabold text-white shadow-lg backdrop-blur-md">
                                                {String(index + 1).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </div>


                                            {/* Location */}
                                            <div className="absolute right-4 top-4">

                                                <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-[10px] font-bold text-white shadow-lg backdrop-blur-md">
                                                    <FaMapMarkerAlt className="text-[#FFC928]" />
                                                    {destination.name}
                                                </div>

                                            </div>


                                            {/* Bottom image content */}
                                            <div className="absolute bottom-0 left-0 right-0 p-5">

                                                <div className="mb-2 flex items-center gap-1">

                                                    {[1, 2, 3, 4, 5].map(
                                                        (star) => (
                                                            <FaStar
                                                                key={star}
                                                                className="text-[10px] text-[#FFC928]"
                                                            />
                                                        )
                                                    )}

                                                    <span className="ml-1 text-[10px] font-semibold text-white/80">
                                                        Hotel
                                                    </span>

                                                </div>

                                                <h3 className="line-clamp-1 text-xl font-extrabold text-white">
                                                    {hotel.name}
                                                </h3>

                                            </div>

                                        </div>


                                        {/* ==========================
                                            CONTENT
                                        ========================== */}

                                        <div className="p-5">

                                            <p className="line-clamp-2 min-h-[44px] text-sm leading-6 text-slate-500">
                                                {cleanDescription}
                                            </p>


                                            {/* Info row */}
                                            <div className="mt-5 grid grid-cols-2 gap-3">

                                                <div className="rounded-xl bg-[#F5F9FE] p-3">

                                                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                                                        Location
                                                    </p>

                                                    <div className="mt-1 flex items-center gap-1.5 text-xs font-bold text-[#174EA6]">
                                                        <FaMapMarkerAlt />
                                                        <span className="truncate">
                                                            {destination.name}
                                                        </span>
                                                    </div>

                                                </div>


                                                <div className="rounded-xl bg-[#FFF9E2] p-3">

                                                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                                                        Starting From
                                                    </p>

                                                    <p className="mt-1 text-sm font-extrabold text-[#0F172A]">
                                                        {price.toLocaleString()}{" "}
                                                        <span className="text-[9px] font-semibold text-slate-400">
                                                            AED
                                                        </span>
                                                    </p>

                                                </div>

                                            </div>


                                            {/* CTA */}
                                            <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">

                                                <span className="text-xs font-semibold text-slate-400">
                                                    View hotel details
                                                </span>

                                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#174EA6] text-white transition duration-300 group-hover:bg-[#FFC928] group-hover:text-[#0F172A]">
                                                    <FaArrowRight className="text-sm transition group-hover:translate-x-0.5" />
                                                </div>

                                            </div>

                                        </div>


                                        {/* Gold hover line */}
                                        <div className="h-1 w-0 bg-gradient-to-r from-[#174EA6] to-[#FFC928] transition-all duration-500 group-hover:w-full" />

                                    </article>

                                );
                            })}

                        </div>

                    ) : (

                        /* =================================================
                           EMPTY STATE
                        ================================================= */

                        <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center">

                            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#EAF3FF]">
                                <FaHotel className="text-3xl text-[#174EA6]" />
                            </div>

                            <h3 className="mt-6 text-2xl font-extrabold text-[#0F172A]">
                                No Hotels Found
                            </h3>

                            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
                                There are currently no hotels available in{" "}
                                {destination.name}. Explore other destinations
                                to find your perfect stay.
                            </p>

                            <button
                                onClick={() => navigate("/destinations")}
                                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#174EA6] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#002B45]"
                            >
                                Browse Destinations
                                <FaArrowRight />
                            </button>

                        </div>

                    )}

                </div>

            </section>


            {/* =====================================================
                DESTINATION CTA
            ===================================================== */}

            <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-20">

                <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[32px] bg-[#002B45] px-6 py-12 sm:px-10 md:px-14 lg:py-16">

                    {/* Decorative glow */}
                    <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#174EA6]/30 blur-3xl" />

                    <div className="absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-[#FFC928]/10 blur-3xl" />

                    {/* Gold accent */}
                    <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

                    <div className="relative z-10 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">

                        <div className="max-w-2xl">

                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC928]">
                                Your Dubai Stay Awaits
                            </p>

                            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-white sm:text-4xl">
                                Ready to Explore{" "}
                                <span className="text-[#FFC928]">
                                    {destination.name}?
                                </span>
                            </h2>

                            <p className="mt-4 text-sm leading-7 text-white/65 sm:text-base">
                                Find the right hotel for your Dubai trip and
                                enjoy a simple, trusted booking experience.
                            </p>

                        </div>


                        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">

                            <button
                                onClick={() => navigate("/hotels")}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFC928] px-6 py-3.5 text-sm font-extrabold text-[#0F172A] shadow-lg transition hover:-translate-y-0.5 hover:bg-[#FFD84A] hover:shadow-xl"
                            >
                                Explore All Hotels
                                <FaArrowRight />
                            </button>

                            <button
                                onClick={() => navigate("/destinations")}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur-md transition hover:bg-white/15"
                            >
                                More Destinations
                            </button>

                        </div>

                    </div>

                </div>

            </section>

        </div>
    );
}